/* eslint-disable prettier/prettier */
// Pretier messeup line breaks and ternaries
import { groupBy } from 'underscore';
import * as Types from './types';

const VALID_STATUS_CODES = [Types.ProductStatus.PUBLISHED as string, Types.ProductStatus.FREE as string];

// Get selectable variant options and return them as a single list
export function getVariantAttributes(selectableAttributes: any[], variant: Types.Variant): Types.MappedVariant {
  // variantOptions are index based. variant.optionCode1 is always the first attribute
  const keys = selectableAttributes.map((obj) => obj.type);

  const mappedAttributes = {
    ...(keys[0] !== undefined && { [keys[0]]: variant.optionCode1 }),
    ...(keys[1] !== undefined && { [keys[1]]: variant.optionCode2 })
  };

  return {
    ...variant,
    variantAttributes: mappedAttributes as Types.VariantAttributes
  };
}

// Map Variant options to usuable options for React
export function variantOptionsToSelectableAttributes(variantOptions: Types.Attribute[]): Types.SelectableVariantAttribute[] {
  const selectableAttributes = variantOptions.map((option) => {
    const key = option.type.code.toLowerCase();

    const values = option.items.map((item) => ({
      code: item.value.code,
      value: item.value.value,
      image: item?.images[0]?.src ?? ''
    }));

    return {
      type: key,
      code: option.type.code,
      name: option.type.value.toLowerCase(),
      values
    };
  });

  return selectableAttributes;
}

// Attributes to Product Symbols
export function mapProductSymbols(symbols: Types.ProductSymbol[]): Types.MappedProductSymbol[] {
  const mappedSymbols = symbols.map((symbol) => ({
    code: symbol.code.code,
    image: symbol?.images[0]?.src ?? '',
    url: symbol.url,
    value: symbol.value.value
  }));

  return mappedSymbols;
}

export function mapUniqueSellingPoints(usp: Types.ProductSymbol[]): Types.UniqueSellingPoint[] {
  const mappedUsps = usp.map((usp) => ({
    code: usp.code.code,
    value: usp.value.value
  }));

  return mappedUsps;
}

// Flatten and group attributes to usable 'Technical' data
function mapTechnicalData(attributes: Types.Attribute[]): Types.MappedTechnicalData[] {
  // See sitecore item: `/sitecore/content/Tenants/CarelLurvink/Main/Data/Products/Product Details/Technical/Default`

  const allowedAttributes = ['1000', '1100', '1400', '1500', '1600', '1700', '1655', '1650', '2200', '3200'];

  const technicalData = Object.entries(attributes)
    .flatMap(([code, attributeList]) => {
      if (allowedAttributes.indexOf(code) === -1) {
        return undefined;
      }

      return attributeList.items.map((listItem) => {
        if (listItem.value.displayOrder < 0) {
          return undefined;
        }

        return {
          code: listItem.code.code,
          image: listItem.images[0]?.src,
          name: listItem.code.value,
          value: listItem.value.value
        };
      });
    })
    .filter(Boolean);

  const groupedData = groupBy(technicalData, 'code');
  const mergedData = Object.entries(groupedData).map(([key, dataValues]) => {
    const name = dataValues[0]?.name ?? '';
    // @ts-expect-error: TS does not infere `.filter(Boolean)`
    const values = dataValues.reduce<string[]>((acc, curr) => [...acc, curr.value], []);
    return {
      code: key,
      name,
      value: values.join(', ')
    };
  });

  return mergedData;
}

// Map product data to usable data
export function mapCatalogProduct(product: Types.ApiProduct): Types.MappedProduct {
  const selectableVariantAttributes = variantOptionsToSelectableAttributes(product.variantOptions);
  const variants = product.variants.map((variant) => getVariantAttributes(selectableVariantAttributes, variant));

  const { variantOptions, symbols = [], ...newProduct } = product;
  const mappedSymbols = mapProductSymbols(symbols);
  // @ts-expect-error: TS and optional chaining / nullish coalescing :(
  const uniqueSellingPoints = mapUniqueSellingPoints(product?.attributes['PDPLUS']?.items ?? []);
  // @ts-expect-error: TS and optional chaining / nullish coalescing :(
  const technicalData = mapTechnicalData(product?.attributes ?? []);


  /**
   * Nasty bit of code especially for Plegt Vos.
   * Their catalog has to be fitlered on product variants rather then products
   * In this case products that do not have a color attribute with value red should be removed
   * unless there are no color variants
   * We do this by creating an array of optioncodes 2 and checking optionCodes1 to see
   * if the combination leads to a valid variant
   */
  const availableOptionCodes2 = product.variants.map((variant) => {
    return variant.optionCode2;
  }, []);

  if (selectableVariantAttributes && selectableVariantAttributes[0]) {
    const filteredAttributes = {
      ...selectableVariantAttributes[0],
      values: selectableVariantAttributes[0].values.filter((attributeValue) =>
        variants.some((variant: Types.Variant) =>
          variant.optionCode1 === attributeValue.code && availableOptionCodes2.includes(variant.optionCode2)
        )
      )
    };
    selectableVariantAttributes[0] = filteredAttributes;
  }
  /**
   * End Plegt Vos code
   */


  const mappedProduct = {
    ...newProduct,
    variants,
    selectableVariantAttributes,
    ...(mappedSymbols && { symbols: mappedSymbols }),
    uniqueSellingPoints,
    technicalData
  };
  return mappedProduct;
}

// Remove products that can't be displayed
export function filterInValidProducts(product: Types.ApiProduct): boolean {
  if (!VALID_STATUS_CODES.includes(product.status)) {
    return false;
  }
  if (!product.variants || product.variants?.length === 0) {
    return false;
  }
  if (!product.visible) {
    return false;
  }

  return true;
}

/**
 * The backend hardcodes `/c/` on several places, breaking Next' internal routing
 *
 * This traverses the json data, looks for 'urlSet.web'
 * and does a string replace `/c/` with `/`
 */
/* eslint-disable prettier/prettier */
function updateUrlSet(obj: JSONValue): JSONValue {
  return typeof obj === 'object' && obj !== null
    ? Array.isArray(obj)
      ? obj.map(updateUrlSet) // Process array elements
      : Object.keys(obj).reduce((acc, key) => {
        acc[key] =
          //@ts-expect-error: -
          key === 'urlSet' && typeof obj[key] === 'object' && 'web' in obj[key]
            ? //@ts-expect-error: -
            { ...obj[key], web: (obj[key] as any).web.replace('/c/', '/') }
            : updateUrlSet(obj[key]);
        return acc;
      }, {} as { [key: string]: JSONValue })
    : obj;
}

export function transformGetPaginatedProductsListResponse(rawResponse: Types.ApiResponse): Types.MappedResponse {
  const { results, ...data } = rawResponse;

  const products = results?.filter(filterInValidProducts).map((product) => mapCatalogProduct(product));
  const responsData = {
    ...data,
    results: products ?? [],
    //@ts-expect-error: -
    ...(data?.pagination ? { pagination: updateUrlSet(data.pagination) } : { pagination: {} }),
    //@ts-expect-error: -
    ...(data?.sort ? { sort: updateUrlSet(data.sort)} : { sort: {}}),
    //@ts-expect-error: -
    ...(data?.facets ? { facets: updateUrlSet(data.facets) } : { facets: {} })
  };
  //@ts-expect-error: -
  return responsData;
}
